// (C) 2022 GoodData Corporation
import { invariant } from "ts-invariant";
import { StringCharacterIterator } from "./fmt_base.js";
/*
 * Utility class to iterate over characters in a string.
 */
class FormatChar {
    constructor(c, literal = false) {
        this.c = c;
        this.escaped = literal;
    }
    /**
     *  @returns the character as a string
     */
    toString() {
        return this.c;
    }
    /*
     * @returns if character is escaped
     */
    isEscaped() {
        return this.escaped;
    }
    /**
     * @returns true if the format character represents a non-escaped, literal character
     */
    isPlacement() {
        return !this.escaped && (this.c === "0" || this.c === "#" || this.c === "?");
    }
    /**
     * @returns true if the format character represents a non-escaped, zero placement character ('0')
     */
    isZeroPlacement() {
        return !this.escaped && this.c === "0";
    }
    /**
     * @returns true if the format character represents a non-escaped, hash placement character ('#')
     */
    isHashPlacement() {
        return !this.escaped && this.c === "#";
    }
    /**
     * @returns true if the format character represents a non-escaped, question-mark placement character ('?')
     */
    isQPlacement() {
        return !this.escaped && this.c === "?";
    }
    /**
     * @returns true if the character represents a digit from 1 to 9 (not 0 because that is placeholder)
     */
    isDigit() {
        return this.c >= "1" && this.c <= "9";
    }
    /**
     * @returns true if the format character represents a non-escaped comma (','); this is used to indicate thousands
     *  separation and value scaling
     */
    isComma() {
        return !this.escaped && this.c === ",";
    }
    /**
     * @returns true if the format character represents a non-escaped decimal delimiter ('.'); this is used to delimit
     *  whole part and fraction part of the format
     */
    isDecimal() {
        return !this.escaped && this.c === ".";
    }
    /**
     * @returns true if the format character represents a non-escaped percent value indicator ('%'); this is used to
     *  indicate percent value formatting (value * 100)
     */
    isPercent() {
        return !this.escaped && this.c === "%";
    }
    /**
     * @returns A value less than 0 if not color tag placeholder, otherwise visual placeholder index
     */
    getColorPlaceholderIndex() {
        const codePoint = this.c.codePointAt(0);
        const startCodePoint = FormatChar.ColorPlaceholderStart.codePointAt(0);
        const endCodePoint = FormatChar.ColorPlaceholderEnd.codePointAt(0);
        if (codePoint !== undefined &&
            startCodePoint !== undefined &&
            endCodePoint !== undefined &&
            codePoint >= startCodePoint &&
            codePoint <= endCodePoint) {
            return codePoint - startCodePoint;
        }
        return -1;
    }
}
// first visual placeholder character (private use area)
FormatChar.ColorPlaceholderStart = "\uE200";
// last visual placeholder (including)
FormatChar.ColorPlaceholderEnd = "\uE2FF";
export { FormatChar };
/**
 * Represents a sequence of format characters used in a custom formatting.
 */
export class FormatString {
    /**
     * Creates a new FormatString instance.
     */
    constructor() {
        this.vec = [];
    }
    /**
     * Appends a format character to the end of the sequence.
     * @param formatChar - The format character to append.
     */
    append(formatChar) {
        this.vec.push(formatChar);
    }
    /**
     * Inserts a format character at the specified index in the sequence.
     * @param idx - The index at which to insert the format character.
     * @param formatChar - The format character to insert.
     */
    insert(idx, formatChar) {
        this.vec.splice(idx, 0, formatChar);
    }
    /**
     * Retrieves a copy of the array of format characters in the sequence.
     * @returns An array of format characters in the sequence.
     */
    getVec() {
        return this.vec.slice();
    }
    /**
     * Returns the length of the sequence (number of format characters).
     * @returns The number of format characters in the sequence.
     */
    length() {
        return this.vec.length;
    }
}
/**
 * Base class for sub-formatters used to format whole and fraction part.
 */
export class PartialNumberFormatter {
    constructor(formatString, digits, placements) {
        this.formatString = formatString;
        this.digits = digits;
        this.placements = placements;
    }
    getDigits() {
        return this.digits;
    }
    getPlacements() {
        return this.placements;
    }
}
/**
 * Formatter for the whole part of a number.
 */
class WholePartFormatter extends PartialNumberFormatter {
    format(number, thousandSeparator, colorPlaceholderValues) {
        const isNegative = number.charAt(0) === "-";
        const numberIterator = new StringCharacterIterator(number, isNegative ? 1 : 0, number.length, number.length);
        const formatIterator = this.formatString.getVec().reverse();
        let result = "";
        let remainingPlacement = this.getPlacements();
        let remainingPadding = this.getDigits();
        let placedDigits = 0;
        const useSeparators = thousandSeparator.length > 0;
        const actualSeparator = thousandSeparator.charAt(0) !== "-" ? thousandSeparator : "0";
        // Stage 1
        while (formatIterator.length > 0 &&
            (numberIterator.hasPrevious() || remainingPadding) &&
            remainingPlacement) {
            const formatChar = formatIterator.shift();
            if (formatChar === undefined) {
                break;
            }
            const placeholderIdx = formatChar.getColorPlaceholderIndex();
            if (placeholderIdx >= 0) {
                if (colorPlaceholderValues === null ||
                    colorPlaceholderValues === undefined ||
                    colorPlaceholderValues.length < placeholderIdx) {
                    continue;
                }
                result += colorPlaceholderValues[placeholderIdx];
                continue;
            }
            if (!formatChar.isPlacement()) {
                result += formatChar;
                continue;
            }
            if (useSeparators && placedDigits === 3) {
                result += actualSeparator;
                placedDigits = 0;
            }
            else {
                let numberChar;
                if (numberIterator.hasPrevious()) {
                    numberChar = numberIterator.previous();
                }
                else {
                    numberChar = "0";
                }
                result += numberChar;
                placedDigits++;
                if (remainingPadding) {
                    remainingPadding--;
                }
            }
            remainingPlacement--;
        }
        // Stage 2
        while (numberIterator.hasPrevious() || remainingPadding) {
            if (useSeparators && placedDigits === 3) {
                result += actualSeparator;
                placedDigits = 0;
            }
            else if (numberIterator.hasPrevious()) {
                result += numberIterator.previous();
                placedDigits++;
                if (remainingPadding) {
                    remainingPadding--;
                }
            }
            else {
                result += "0";
                placedDigits++;
                remainingPadding--;
            }
        }
        if (isNegative) {
            result += "-";
        }
        // Stage 3
        while (formatIterator.length > 0) {
            const formatChar = formatIterator.shift();
            if (formatChar === undefined) {
                break;
            }
            const placeholderIdx = formatChar.getColorPlaceholderIndex();
            if (placeholderIdx >= 0) {
                if (colorPlaceholderValues == null || colorPlaceholderValues.length < placeholderIdx) {
                    continue;
                }
                result += colorPlaceholderValues[placeholderIdx];
                continue;
            }
            if (formatChar.isHashPlacement()) {
                continue;
            }
            else if (formatChar.isQPlacement()) {
                result += " ";
            }
            else {
                result += formatChar;
            }
        }
        // iteration was right-to-left...
        return result.split("").reverse().join("");
    }
}
WholePartFormatter.NoThousandsSeparators = "";
export { WholePartFormatter };
/**
 * Formatting statistics for the fraction part of a number.
 */
export class FractionFormatStats {
    constructor() {
        this.placedDigits = 0;
        this.placedLiteralDigits = 0;
        this.placedEscapedCharacters = 0;
    }
}
/**
 * Formatter for the fraction part of a number.
 */
export class FractionPartFormatter extends PartialNumberFormatter {
    format(number, stats, colorPlaceholderValues = null) {
        if (!this.digits) {
            return "";
        }
        const formatIterator = this.formatString.getVec().slice();
        const numberIterator = new StringCharacterIterator(number, 0, number.length);
        let digitsRemaining = this.digits;
        let placedDigits = 0;
        let placedLiteralDigits = 0;
        let placedEscapedCharacters = 0;
        let result = "";
        let zeroAccumulator = "";
        while (digitsRemaining) {
            const formatChar = formatIterator.shift();
            // if this bombs then again compilation does not work correctly; a string of placement characters
            // at the start of the fraction part is the number of digits = precision
            invariant(formatChar.isPlacement());
            const digit = numberIterator.hasNext() ? numberIterator.next() : "0";
            invariant(!isNaN(Number(digit)), "Fraction part should only include digits");
            if (formatChar.isHashPlacement()) {
                // Special handling when placing digits into '#' placement to trim trailing zeroes
                if (digit === "0") {
                    zeroAccumulator += digit;
                }
                else {
                    // Flush accumulated zeroes
                    result += zeroAccumulator;
                    placedDigits += zeroAccumulator.length;
                    zeroAccumulator = "";
                    result += digit;
                    placedDigits++;
                }
            }
            else {
                // Flush and reset the accumulated zeroes
                result += zeroAccumulator;
                placedDigits += zeroAccumulator.length;
                zeroAccumulator = "";
                result += digit;
                placedDigits++;
            }
            digitsRemaining--;
        }
        if (this.placements > this.digits) {
            // There are more placement characters than precision.
            // The formatter does not drop trailing zeroes, so they should be appended to the result.
            result += zeroAccumulator;
            placedDigits += zeroAccumulator.length;
            zeroAccumulator = "";
        }
        // All digits from the number string are either processed or can be discarded.
        // The format string may contain more stuff, such as placement characters or 'random' strings.
        while (formatIterator.length > 0) {
            const formatChar = formatIterator.shift();
            const placeholderIdx = formatChar.getColorPlaceholderIndex();
            if (placeholderIdx >= 0) {
                // Handle visual tag placeholder
                if (colorPlaceholderValues === null || colorPlaceholderValues.length <= placeholderIdx) {
                    continue;
                }
                result += colorPlaceholderValues[placeholderIdx];
            }
            else if (!formatChar.isHashPlacement()) {
                if (formatChar.isQPlacement()) {
                    result += " ";
                }
                else {
                    placedEscapedCharacters += formatChar.isEscaped() ? 1 : 0;
                    placedLiteralDigits += formatChar.isDigit() ? 1 : 0;
                    result += formatChar.toString();
                }
            }
        }
        stats.placedDigits = placedDigits;
        stats.placedLiteralDigits = placedLiteralDigits;
        stats.placedEscapedCharacters = placedEscapedCharacters;
        return result;
    }
}
//# sourceMappingURL=fmt_number_commons.js.map